'use client';

import { Drawer, Flex, Icon, Portal, Tag, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaFilter } from 'react-icons/fa6';
import { api } from '@/services/api';
import { CategoryProps } from '@/types/Category';
import FilterBox from './FilterBox';
import { useRouter, useSearchParams } from 'next/navigation';

// Só estes parâmetros do URL são filtros — os fixos e as características,
// cujo id é sempre um UUID. Tudo o resto (utm_*, gclid, fbclid, srsltid,
// mc_cid…) vem de campanhas: antes era tratado como filtro e a lista ficava
// vazia. Agora é ignorado como filtro, mas mantém-se no URL para não se
// perder a origem da visita (Google Analytics, Meta).
const FIXED_FILTER_KEYS = ['marcas', 'cores', 'tamanhos', 'variacoes'];
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isFilterKey = (key: string) =>
  FIXED_FILTER_KEYS.includes(key) || UUID_REGEX.test(key);

// Parâmetros que o URL tem de manter mas que não são filtros nem pesquisa.
const isPassthroughKey = (key: string) =>
  !isFilterKey(key) && key !== 'q' && key !== 'price_min' && key !== 'price_max';

interface FilterData {
  title: string;
  url_slug: string;
  data:
    | Array<{ id: string; name: string; count: number }>
    | { min: number; max: number }
    | Array<{ id: string; name: string; hex?: string; count: number }>;
}

const FilterButton = ({
  categories,
  handleFilter,
  searchQuery,
}: {
  categories: CategoryProps[];
  handleFilter: (url: string) => void;
  searchQuery?: string;
}) => {
  const [filterData, setFilterData] = useState<FilterData[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: string[];
  }>({});
  const [isLoading, setIsLoading] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();

  const handleSelectFilter = useCallback(
    (filterType: string, value: string | number[]) => {
      setSelectedFilters((prev) => {
        const currentValues = prev[filterType] || [];
        if (Array.isArray(value)) {
          return {
            ...prev,
            [filterType]: value.map((v) => v.toString()),
          };
        }
        if (currentValues.includes(value)) {
          return {
            ...prev,
            [filterType]: currentValues.filter((v) => v !== value),
          };
        } else {
          return {
            ...prev,
            [filterType]: [...currentValues, value],
          };
        }
      });
    },
    [],
  );

  const handleRemoveFilter = useCallback(
    (filterType: string, value: string) => {
      setSelectedFilters((prev) => {
        const currentValues = prev[filterType] || [];
        return {
          ...prev,
          [filterType]: currentValues.filter((v) => v !== value),
        };
      });
    },
    [],
  );

  const handleFilterToSearch = useCallback(
    (filters: { [key: string]: string[] }) => {
      const params = new URLSearchParams();

      Object.keys(filters).forEach((key) => {
        if (filters[key].length === 0) return;

        if (key === 'preco' && filters[key].length === 2) {
          params.append('price_min', filters[key][0]);
          params.append('price_max', filters[key][1]);
          return;
        }

        params.append(key, filters[key].join(','));
      });

      return params.toString();
    },
    [],
  );

  useEffect(() => {
    const fetchFilters = async () => {
      setIsLoading(true);
      try {
        // Só a categoria mais específica (não toda a cadeia de
        // antepassados) — para os filtros disponíveis corresponderem
        // exatamente aos produtos mostrados na página.
        const query =
          categories.length > 0
            ? `?categories=${categories[categories.length - 1].id}`
            : '';
        const response = await api.get(`/products/filter/list${query}`);
        setFilterData(response.data);
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };

    fetchFilters();
  }, [categories]);

  // Último filtro aplicado à lista. Só se volta a aplicar (o que repõe a
  // página em 1) quando o filtro muda mesmo — sincronizar o URL cria um
  // objeto de filtros novo com o mesmo conteúdo, e sem esta guarda isso
  // reiniciava o scroll infinito.
  const lastAppliedFilterRef = useRef<string | null>(null);

  useEffect(() => {
    if (Object.keys(selectedFilters).length === 0) {
      lastAppliedFilterRef.current = null;
      return;
    }
    const query = handleFilterToSearch(selectedFilters);
    if (query === lastAppliedFilterRef.current) return;
    lastAppliedFilterRef.current = query;
    handleFilter(query);
  }, [handleFilter, selectedFilters, handleFilterToSearch]);

  useEffect(() => {
    // Preserva o 'q' (texto de pesquisa) ao reescrever o URL com os filtros
    // de facetas — sem isto, o URL perdia o '?q=...' assim que este efeito
    // corria, o que fazia a página re-executar sem pesquisa e mostrar o
    // catálogo todo em vez dos resultados da pesquisa.
    const params = new URLSearchParams(handleFilterToSearch(selectedFilters));
    if (searchQuery) params.set('q', searchQuery);
    // Mantém os parâmetros de campanha que já estavam no URL.
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (isPassthroughKey(key) && !params.has(key)) params.append(key, value);
    });
    router.push(`?${params.toString()}`);
  }, [router, selectedFilters, handleFilterToSearch, searchQuery]);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    const newSelectedFilters: { [key: string]: string[] } = {};

    Object.keys(params).forEach((key) => {
      // 'q' é o texto de pesquisa (tratado à parte via searchQuery), não um
      // filtro de facetas — sem isto, entrava sempre em selectedFilters
      // numa página de pesquisa, o que disparava handleFilter -> setPage(1)
      // em loop e impedia avançar para outras páginas de resultados.
      if (!isFilterKey(key)) return;
      newSelectedFilters[key] = params[key].split(',');
    });

    if (params.price_min && params.price_max) {
      newSelectedFilters.preco = [params.price_min, params.price_max];
    }

    setSelectedFilters(newSelectedFilters);
  }, [searchParams]);

  return (
    <Drawer.Root>
          <Drawer.Backdrop />
          <Drawer.Trigger asChild>
        <Flex
          as="button"
          align="center"
          gap="8px"
          px="14px"
          h="38px"
          borderRadius="full"
          bg="rgba(0,176,202,0.08)"
          border="1.5px solid rgba(0,176,202,0.25)"
          cursor="pointer"
          transition="all 0.2s"
          _hover={{ bg: 'rgba(0,176,202,0.14)', borderColor: 'rgba(0,176,202,0.4)' }}
          _active={{ bg: 'rgba(0,176,202,0.2)' }}
        >
          <Icon w="13px" h="13px" color="#00B0CA"><FaFilter /></Icon>
          <Text fontSize="13px" fontWeight="600" color="#00B0CA">Filtrar</Text>
        </Flex>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
            zIndex="30"
            borderRadius="20px"
            boxShadow="0 8px 40px rgba(0,0,0,0.18)"
            right="10px"
            top="10px"
            bottom="10px"
            h="calc(100svh - 20px)"
            overflow="hidden"
            border="2px solid"
            borderColor={{ base: 'rgba(0,176,202,0.15)', _dark: 'rgba(0,176,202,0.12)' }}
          >
          <Drawer.CloseTrigger />
          <Drawer.Header>
            <Drawer.Title>Filtros</Drawer.Title>
          </Drawer.Header>
          <Flex gap={2} flexWrap="wrap" px={2} mb={2}>
            {Object.keys(selectedFilters).length > 0 &&
              Object.keys(selectedFilters).map((key) =>
                selectedFilters[key].map((value) => (
                  <Tag.Root
                    key={`${key}-${value}`}
                    variant={'solid'}
                    bg="#00B0CA"
                    color="#fff"
                    borderRadius="full"
                    boxShadow="md"
                  >
                    <Tag.Label>{value}</Tag.Label>
                    <Tag.EndElement
                      ml={1}
                      onClick={() => handleRemoveFilter(key, value)}
                      _hover={{ cursor: 'pointer' }}
                    >
                      <Tag.CloseTrigger _hover={{ cursor: 'pointer' }} />
                    </Tag.EndElement>
                  </Tag.Root>
                )),
              )}
          </Flex>
          {!isLoading && (
            <Drawer.Body>
              {filterData.length > 0 &&
                filterData.map((filter) => (
                  <FilterBox
                    key={filter.url_slug}
                    filterData={
                      filter.data as Array<{
                        id: string;
                        name: string;
                        count: number;
                      }>
                    }
                    title={filter.title}
                    slug={filter.url_slug}
                    handleSelectFilter={handleSelectFilter}
                    selectedFilters={selectedFilters}
                  />
                ))}
            </Drawer.Body>
          )}
          <Drawer.Footer>
            <Text fontSize="10px" color={{ base: 'rgba(0,0,0,0.3)', _dark: 'rgba(255,255,255,0.25)' }}>
              {filterData.length} grupo{filterData.length !== 1 ? 's' : ''} de filtros
            </Text>
          </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default FilterButton;
